import { Button, ButtonProps } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { PoolTooltip } from '@synergy/components-next';
import { ReactElement, ReactNode } from 'react';

import {
    PaloActionAppearance,
    PaloDocumentPhase,
    PaloFlowKind,
    paloActionAppearance,
    paloMainAction,
    paloSaveBlocked,
} from './palo-main-action-rule';
import { usePaloDirty } from '../../common/components/palo/palo-dirty-context';

export * from './palo-main-action-rule';

/** The appearance of a mask's own flow button. */
export const usePaloFlowButtonProps = (phase: PaloDocumentPhase, kind: PaloFlowKind): PaloActionAppearance =>
    paloActionAppearance(phase, usePaloDirty(), kind);

/**
 * Whether this is the one action the head shows (#2206 rule 3).
 *
 * For buttons a mask builds itself rather than through PaloFlowAction — a
 * split button, say. They have to ask, because nothing else can hide them.
 */
export const usePaloIsMainAction = (phase: PaloDocumentPhase, kind: PaloFlowKind): boolean =>
    paloMainAction(phase, usePaloDirty()) === kind;

export interface PaloFlowActionProps extends Omit<ButtonProps, 'variant' | 'color' | 'sx'> {
    phase: PaloDocumentPhase;
    kind: PaloFlowKind;
    /** Why the action cannot be used right now — shown on hover (FRM-18). */
    blockedReason?: ReactNode;
    /**
     * The action saves the document itself before it acts. Unsaved input then
     * does not push it aside for "Speichern": it would only cost a click
     * (#2258 R-180, sending an inquiry).
     */
    savesFirst?: boolean;
}

/**
 * A mask's own next step, in the appearance its phase gives it.
 *
 * A disabled button says nothing about why, which is the one thing the user
 * needs at that moment; a reason handed in here is shown on hover.
 */
export const PaloFlowAction = (props: PaloFlowActionProps): ReactElement | null => {
    const { phase, kind, blockedReason, savesFirst, ...buttonProps } = props;
    const dirty = usePaloDirty() && !savesFirst;
    const appearance = paloActionAppearance(phase, dirty, kind);

    // #2206 rule 3: the row carries the one action that is the next step, and
    // nothing else. A step the document is not at belongs in the menu, which
    // the mask fills itself — rendered here it would stand beside the step
    // that is due and compete with it.
    //
    // `revoke` is exempt: taking a document back is never the next step, so the
    // rule would hide it everywhere, and no mask offers it anywhere else yet.
    // It stays white beside the main action until it has a place in the menu.
    //
    // Not on a document that has already been taken back, though: a cancelled
    // invoice kept offering "Rechnung stornieren", and on the overview step it
    // was not even greyed out (#2207, N-1). A cancelled document has no action
    // due at all (FRM-18).
    const hidden = 'revoke' === kind ? 'canceled' === phase : paloMainAction(phase, dirty) !== kind;

    if (hidden) {
        return null;
    }

    return (
        <PoolTooltip title={buttonProps.disabled && blockedReason ? blockedReason : ''}>
            <Button size="small" {...appearance} {...buttonProps} />
        </PoolTooltip>
    );
};

export interface PaloSaveActionProps {
    phase: PaloDocumentPhase;
    /**
     * Set where the mask compares its form against the document as the server
     * has it (PaloDirtyProvider with a loaded document). Only then is "nothing
     * changed" trustworthy enough to switch saving off; without that baseline
     * the button stays usable and merely steps back visually, because a save
     * the user cannot reach is worse than one offered too often.
     */
    comparesToServer?: boolean;
    /** Another action saves as well and is the filled one; saving stands beside it, plain. */
    behind?: boolean;
    onSave: () => void;
}

/**
 * Saving, in the appearance its phase gives it.
 *
 * Filled while there is something to save, white afterwards, and switched off
 * on a document that is closed — a completed invoice offered saving as its
 * loudest action, on a form it would not have accepted (R-90).
 */
export const PaloSaveAction = ({
    phase,
    comparesToServer,
    behind,
    onSave,
}: PaloSaveActionProps): ReactElement | null => {
    const dirty = usePaloDirty();
    const blocked = paloSaveBlocked(phase, dirty, comparesToServer);

    // Saving is offered while there is something to save, and then it is the
    // step that is due. On a document with nothing open it was a switched-off
    // button beside the live one (ERPNext shows none either); discarding is
    // what the leave dialog is for.
    //
    // Hidden only where "nothing changed" can be trusted: on a closed document,
    // or where the mask compares against the server. Without that baseline the
    // button stays, because a save the user cannot reach is worse than one
    // offered too often.
    const trustworthy = 'done' === phase || 'canceled' === phase || true === comparesToServer;

    if (trustworthy && 'save' !== paloMainAction(phase, dirty)) {
        return null;
    }

    return (
        <PoolTooltip
            title={undefined === blocked ? '' : <FormattedMessage id={`app.palo.action.blocked.${blocked}`} />}
        >
            <Button
                size="small"
                disabled={undefined !== blocked}
                onClick={onSave}
                {...paloActionAppearance(phase, dirty && !behind, 'save')}
            >
                <FormattedMessage id="app.common.save" />
            </Button>
        </PoolTooltip>
    );
};
